import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ANDROID_URL,
    APK_URL,
    DISMISS_KEY,
    IOS_URL,
    getDownloadOptions,
    shouldShowMobileAppModal,
} from '../utils/mobileAppLinks';
import useApkInfo from '../hooks/useApkInfo';

export default function MobileAppModal() {
    const { t } = useTranslation('common');
    const [visible, setVisible] = useState(false);
    const [hintOpen, setHintOpen] = useState(false);
    const [logoFailed, setLogoFailed] = useState(false);

    const options = getDownloadOptions();
    const apk = useApkInfo();
    const showApk = options.apk && apk.available;

    useEffect(() => {
        if (!shouldShowMobileAppModal()) return undefined;

        const timer = setTimeout(() => {
            setVisible(true);
            localStorage.removeItem('firstLogin');
        }, 2000);
        return () => clearTimeout(timer);
    }, []);

    const dismiss = () => {
        sessionStorage.setItem(DISMISS_KEY, 'true');
        setVisible(false);
    };

    if (!visible) return null;

    const logoSrc = `${process.env.PUBLIC_URL || ''}/logo-horizontal-light.svg`;

    return (
        <>
            <style>{`
                @keyframes mobileAppSlideUp {
                    from { transform: translateY(100%); opacity: 0.6; }
                    to { transform: translateY(0); opacity: 1; }
                }
            `}</style>
            <div
                role="presentation"
                onClick={dismiss}
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: '#00000080',
                    zIndex: 10000,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                }}
            >
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="mobile-app-modal-title"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        width: '100%',
                        maxWidth: 480,
                        background: '#fff',
                        borderRadius: '16px 16px 0 0',
                        padding: '20px 20px calc(20px + env(safe-area-inset-bottom, 0px))',
                        boxShadow: '0 -8px 32px rgba(0,0,0,0.18)',
                        animation: 'mobileAppSlideUp 0.3s ease',
                    }}
                >
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 16,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {!logoFailed ? (
                                <img
                                    src={logoSrc}
                                    alt=""
                                    onError={() => setLogoFailed(true)}
                                    style={{ height: 28, width: 'auto' }}
                                />
                            ) : (
                                <span style={{ fontSize: 24 }} aria-hidden>🏆</span>
                            )}
                            <span id="mobile-app-modal-title" style={{ fontWeight: 700, fontSize: 17 }}>
                                {t('brandName')}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={dismiss}
                            aria-label={t('close')}
                            style={{
                                border: 'none',
                                background: 'none',
                                fontSize: 22,
                                lineHeight: 1,
                                cursor: 'pointer',
                                color: '#64748b',
                                padding: 4,
                            }}
                        >
                            ✕
                        </button>
                    </div>

                    <h2 style={{ margin: '0 0 10px', fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
                        {t('mobileApp.title')}
                    </h2>
                    <p style={{ margin: '0 0 20px', fontSize: 15, lineHeight: 1.5, color: '#475569' }}>
                        {t('mobileApp.desc')}
                    </p>

                    {options.googlePlay && (
                        <div style={{ marginBottom: 10 }}>
                            <button
                                type="button"
                                onClick={() => window.open(ANDROID_URL, '_blank', 'noopener,noreferrer')}
                                style={{
                                    width: '100%',
                                    minHeight: 48,
                                    padding: '14px 16px',
                                    border: 'none',
                                    borderRadius: 12,
                                    background: '#34a853',
                                    color: '#fff',
                                    fontSize: 16,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                }}
                            >
                                📱 {t('mobileApp.androidBtn')}
                            </button>
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4, textAlign: 'center' }}>
                                {t('mobileApp.androidVpnHint')}
                            </p>
                        </div>
                    )}

                    {showApk && (
                        <div style={{ marginBottom: 10 }}>
                            <a
                                href={APK_URL}
                                download
                                style={{
                                    display: 'block',
                                    boxSizing: 'border-box',
                                    width: '100%',
                                    minHeight: 48,
                                    padding: '14px 16px',
                                    border: '2px solid #34a853',
                                    borderRadius: 12,
                                    background: '#fff',
                                    color: '#166534',
                                    fontSize: 16,
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    textDecoration: 'none',
                                }}
                            >
                                ⬇️ {t('mobileApp.apkBtn')}
                            </a>
                            <p style={{ fontSize: 11, color: '#888', marginTop: 4, textAlign: 'center' }}>
                                {t('mobileApp.apkHint')}
                            </p>
                        </div>
                    )}

                    {options.testFlight && (
                        <button
                            type="button"
                            onClick={() => window.open(IOS_URL, '_blank', 'noopener,noreferrer')}
                            style={{
                                width: '100%',
                                padding: '14px 16px',
                                marginBottom: 10,
                                border: 'none',
                                borderRadius: 12,
                                background: '#000000',
                                color: '#fff',
                                fontSize: 16,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            🍎 {t('mobileApp.iosBtn')}
                        </button>
                    )}

                    <div style={{
                        marginTop: 8,
                        marginBottom: 16,
                        padding: '12px 14px',
                        background: '#fffbeb',
                        borderRadius: 10,
                        border: '1px solid #fde68a',
                    }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#b45309', marginBottom: 6 }}>
                            ⚠️ {t('mobileApp.betaNote')}
                        </div>
                        <button
                            type="button"
                            onClick={() => setHintOpen((v) => !v)}
                            style={{
                                border: 'none',
                                background: 'none',
                                padding: 0,
                                fontSize: 13,
                                color: '#2563eb',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textAlign: 'left',
                            }}
                        >
                            [i] {t('mobileApp.whyNonStandard')}
                        </button>
                        {hintOpen && (
                            <p style={{
                                margin: '10px 0 0',
                                fontSize: 13,
                                lineHeight: 1.55,
                                color: '#57534e',
                            }}>
                                {options.testFlight && !options.googlePlay ? t('mobileApp.iosHint') : t('mobileApp.androidHint')}
                                {' '}
                                {t('mobileApp.sorry')}
                            </p>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={dismiss}
                        style={{
                            width: '100%',
                            border: 'none',
                            background: 'none',
                            padding: '10px 0 0',
                            fontSize: 15,
                            color: '#64748b',
                            cursor: 'pointer',
                            textAlign: 'center',
                        }}
                    >
                        {t('mobileApp.continueWeb')}
                    </button>
                </div>
            </div>
        </>
    );
}
